import * as React from 'react';
import { Link } from 'react-router-dom';
import { FieldPath, FieldPathValue, UseFormReturn, useWatch } from 'react-hook-form';
import If from 'Views/Components/If/If';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInput } from 'Views/Components/New/PhoneInput';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { UserEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { DetailFieldsReadOnly } from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/DetailFieldsReadOnly';
import { AccountDetailsTabHeader } from 'Views/Components/_HumanWritten/AccountDetails/AccountDetailsTabHeader';
import { FormErrors } from 'Util/_HumanWritten/Errors/FormErrors';
import { appConfigStore } from 'Models/AppConfigStore';
import { useIntl } from 'react-intl';
import { camelCase } from 'Util/StringUtils';
import { AccountFormValues } from 'Modules/UserAccount/Hooks/useAccountEditSession';
import { useUserDisplayNamesStore } from 'Global/User/UserDisplayNameStore';
import { showCompanyNameField } from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/AccountFieldsUtils';

export interface UserAccountFieldsProps {
	/** The fetched user — drives the read-only view, the password link and the request flow. */
	user: UserEntity;
	/** The edit session's form; every editable scalar account field lives here. */
	form: UseFormReturn<AccountFormValues>;
	mode: EntityFormMode;
	setMode: (mode: EntityFormMode) => void;
	staffView?: boolean;
	errors: FormErrors<UserEntity>;
	canEdit?: boolean;
	onConvertGuest?: () => void;
	/**
	 * Optional customer-type field rendered among the account fields (the staff user detail
	 * page supplies its UserTypeSection here). Absent on the customer's own account page.
	 */
	userTypeSlot?: React.ReactNode;
}

function DetailFields({
	user,
	form,
	mode,
	setMode,
	staffView,
	errors,
	canEdit = true,
	onConvertGuest,
	userTypeSlot,
}: UserAccountFieldsProps) {
	const intl = useIntl();
	const { userDisplayNames } = useUserDisplayNamesStore();
	const values = useWatch({ control: form.control });
	const { userPostcodeEnabled, userPhoneEnabled, userPhoneRequired } = appConfigStore;

	if (mode === EntityFormMode.VIEW) {
		return (
			<DetailFieldsReadOnly
				user={user}
				mode={mode}
				setMode={setMode}
				updatePasswordButton={updatePasswordLink(staffView ?? false, user)}
				staffView={staffView}
				canEdit={canEdit}
				onConvertGuest={onConvertGuest}
				userTypeSlot={userTypeSlot}
			/>
		);
	}

	// Typed with react-hook-form's own path types: `keyof AccountFormValues` looks equivalent but
	// setValue resolves its value type through FieldPathValue, which TS cannot see as the same type
	// for a generic key.
	const setField = <K extends FieldPath<AccountFormValues>>(
		field: K,
		value: FieldPathValue<AccountFormValues, K>,
	) => {
		form.setValue(field, value, { shouldDirty: true });
	};

	// The business flag must follow the form's (possibly just-changed) type on the staff page.
	// Fall back to the fetched user's linked object when the display-name store lacks the id.
	const currentType =
		userDisplayNames.find(x => x.id === values.userDisplayNameId) ??
		(user.userDisplayNameId === values.userDisplayNameId ? user.userDisplayName : undefined);
	// The company name is read from the fetched user, not the live form value: gating on what is
	// currently typed would make the input vanish the moment someone clears it mid-edit.
	const isBusinessAccount = showCompanyNameField(currentType, user.companyName, { staffView });

	const postcodeLabel = intl.formatMessage({
		id: 'user-account__postcode',
		defaultMessage: 'Postcode',
	});

	return (
		<div className="user-account-fields user-account-fields--edit">
			<AccountDetailsTabHeader headerText="Account details" />
			<If condition={isBusinessAccount}>
				<TextInput
					id="company-name"
					className="company-name"
					label="Company name"
					required={currentType?.companyNameFieldRequired ?? false}
					placeholder="Company name"
					value={values.companyName ?? ''}
					onChange={e => setField('companyName', e.target.value)}
					error={errors.companyName}
				/>
			</If>
			<TextInput
				id="first-name"
				className="first-name"
				label={isBusinessAccount ? 'Contact first name' : 'First name'}
				required
				placeholder={isBusinessAccount ? 'Contact first name' : 'First name'}
				value={values.firstName ?? ''}
				onChange={e => setField('firstName', e.target.value)}
				error={errors.firstName}
			/>
			<TextInput
				id="last-name"
				className="last-name"
				label={isBusinessAccount ? 'Contact last name' : 'Last name'}
				required
				placeholder={isBusinessAccount ? 'Contact last name' : 'Last name'}
				value={values.lastName ?? ''}
				onChange={e => setField('lastName', e.target.value)}
				error={errors.lastName}
			/>
			{/*
				Password is non-editable inline (changes go through /update-password). Show a
				static mask so the field reads "filled in" without leaking length, and put the
				Update affordance next to it.
			*/}
			<div className="user-profile-password flex gap-2 items-end">
				<div className="flex-1">
					<TextInput id="password" label="Password" value="••••••" readOnly required />
				</div>
				{updatePasswordLink(staffView ?? false, user)}
			</div>
			<TextInput
				id="email"
				className="email"
				label="Email address"
				required
				placeholder="Email address"
				disabled={store.isStaff && !staffView}
				value={values.email ?? ''}
				onChange={e => setField('email', e.target.value)}
				error={errors.email}
			/>
			{userPhoneEnabled && (
				<PhoneInput
					id="phone"
					className="phone"
					label="Mobile number"
					required={userPhoneRequired && !store.isStaff}
					placeholder="Mobile number"
					value={values.phone ?? ''}
					onChange={phone => setField('phone', phone)}
					error={errors.phone}
				/>
			)}
			{userPostcodeEnabled && (
				<TextInput
					id="postcode"
					className="postcode"
					label={postcodeLabel}
					required={userPostcodeEnabled}
					placeholder={postcodeLabel}
					value={values.postcode ?? ''}
					onChange={e => setField('postcode', e.target.value)}
					error={errors.postcode}
				/>
			)}
			{/*
				Marketing consent lives on the Communications tab, not here. It was editable in both
				places against the same column, and this copy also flattened it: the value is tri-state
				(null = never asked, which the Brevo sync treats as "skip"), and the form read it as
				`=== true` and wrote the result back on every save — so editing a phone number recorded
				a decision the customer had never made.
			*/}
			{userTypeSlot}
		</div>
	);
}

export function updatePasswordLink(staffView: boolean, user: UserEntity) {
	return (
		<Link
			to="/update-password"
			state={
				staffView
					? {
							email: user.email,
							name: user.firstName,
							role: (user.userGroups.map(x => camelCase(x.role?.name ?? '')) ?? [''])[0],
						}
					: undefined
			}
			className="btn btn--solid btn--secondary btn--sm new-style hide-underline update-password"
		>
			Update
		</Link>
	);
}

export default DetailFields;
